/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState, useContext } from "react";
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
// Styles
import {
  ActionButton,
  Checkbox,
  CheckboxGroup,
  SearchField,
} from "@adobe/react-spectrum";

import Asterisk from "@spectrum-icons/workflow/Asterisk";
import Alert from "@spectrum-icons/workflow/Alert";
import ChevronDown from "@spectrum-icons/workflow/ChevronDown";
// Lodash
import lodash, { get } from "lodash";

// Redux
import { useSelector } from "react-redux";

import { FormContext } from "../context/FormsContext";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

import styles from "./css/MultiSelectRenderer.module.scss";

/**
 * Tester
 */
export const MultiSelectRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("MultiSelect")
);

type optionsType = {
  // form element name
  name: string;
  // label to be shown for the editor
  label?: string;
  // boolean value to disable the multi select
  disabled?: boolean;
  // placeholder for the multi select
  placeholder?: string;
  // multi select options
  rows: any;
  // error text to be shown
  errors?: {
    required: string;
  };
  // search options
  search?: {
    isVisible: true;
    placeholder: string;
    spectrumProps: any;
    noResults: string;
  };
};

function MultiSelectRenderer({
  uischema,
  schema,
  visible,
  enabled,
}: RendererProps) {
  const {
    name,
    label = "label",
    disabled,
    placeholder = "Select",
    rows,
    errors = {
      required: "This is a required field",
    },
    search = {
      isVisible: false,
      placeholder: "",
      spectrumProps: {},
      noResults: "",
    },
  } = uischema?.options as optionsType;
  const required = (schema?.required || []).indexOf(name) > -1 || false;
  const jsonFormContext = useJsonForms();
  const [touched, setTouched] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [checkboxData, setCheckboxData] = useState<any>([]);
  const [_, setContext] = useContext<any>(FormContext);
  const [list, setList] = useState(rows);
  const data = jsonFormContext?.core?.data || {};
  const { config: { formTouched = false } = {} } = jsonFormContext || {};
  const hasTouched = formTouched || touched;
  const menuRef: any = useRef(null);

  /** callback to set entire selected  items back to main page  */
  const multiselectCallBack =
    jsonFormContext?.config?.lifecycleHooks?.onSuccessCallbacks
      ?.multiselectCallBack;
  useEffect(() => {
    if (multiselectCallBack) {
      /** This callback sets/unset the multiselect key items  to  page component */
      multiselectCallBack(name, checkboxData);
    }
  }, [checkboxData]);

  const closeMenu = () => {
    setMenuOpen(false);
    setTouched(true);
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      setMenuOpen(true);
    }
  };

  const handleChange = (val: any) => {
    setSelected(val);
    setContext((oldValues: any) => ({
      ...oldValues,
      data: { ...data, ...{ [name]: val.length ? val : undefined } },
    }));
  };

  useEffect(() => {
    if (data[name]) {
      setSelected(data[name]);
    }
  }, []);

  useEffect(() => {
    // If the rows get changed from outside, sync it
    if (!lodash.isEqual(rows, list)) {
      setList(rows);
    }
  }, [rows]);

  useEffect(() => {
    const onOutsidePress = (e: Event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    const onEscPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isMenuOpen) {
          closeMenu();
        }
      }
    };
    document.addEventListener("click", onOutsidePress, true);
    document.addEventListener("mousedown", onOutsidePress);
    document.addEventListener("keyup", onOutsidePress);
    document.addEventListener("keyup", onEscPress);

    return () => {
      // Cleanup the event listener
      document.removeEventListener("click", onOutsidePress, true);
      document.removeEventListener("mousedown", onOutsidePress);
      document.removeEventListener("keyup", onOutsidePress);
      document.removeEventListener("keyup", onEscPress);
    };
  }, [isMenuOpen]);

  const hasError = required && hasTouched && !selected.length;

  const selectedValues = selected.join(", ");
  /**
   * method to track checkbox changes with in checkbox group to emit item to main components
   * @param checkedProps
   */
  const setCheckedValues = (checkedProps: any) => {
    const { val: isChecked, item: itemObject } = checkedProps;
    const selectedCheckboxDataCopy = [...checkboxData];
    if (isChecked) {
      selectedCheckboxDataCopy.push({
        id: itemObject.id,
        name: itemObject.name,
      });
      setCheckboxData(selectedCheckboxDataCopy);
    } else {
      const arrayIndex = selectedCheckboxDataCopy.findIndex(
        (checkItem) => checkItem.id === itemObject?.id
      );
      if (arrayIndex) selectedCheckboxDataCopy.splice(arrayIndex, 1);
      setCheckboxData(selectedCheckboxDataCopy);
    }
  };
  const validationState = () => {
    if (required && hasTouched) {
      if (selected.length) {
        return "valid";
      }
      return "invalid";
    }
    return undefined;
  };

  /**
   * function to be called on search
   * filters the rows based on the search term
   * @param searchTerm: string
   */
  const searchHandler = (searchTerm: any) => {
    const newList = rows.filter((item: any) => {
      if (item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      return false;
    });
    setList(newList);
  };

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    // grab the scope from the contextRule
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    // get the redux store state based on the scope provided
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation: any = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  return (
    <div
      className={styles.outer_wrapper}
      id={`form-input-${name}`}
      hidden={!componentVisibility}
    >
      <div className={styles.label}>
        {label}
        {required && (
          <>
            &nbsp;&nbsp;
            <Asterisk UNSAFE_className={styles.required} />
          </>
        )}
      </div>
      <div ref={menuRef}>
        <ActionButton
          data-testid="multiselect-action-btn"
          isDisabled={disabled || !componentEnabled}
          UNSAFE_className={`${styles.custom_list_picker_button} ${
            hasError ? styles.invalid : ""
          }`}
          onPress={toggleMenu}
        >
          {!selectedValues ? (
            <span className={styles.custom_list_picker_placeholder}>
              {placeholder}
            </span>
          ) : (
            <span className={styles.custom_list_picker_values}>
              {selectedValues}
            </span>
          )}
          {hasError && <Alert UNSAFE_className={styles.error_icon} />}
          <ChevronDown UNSAFE_className={styles.down_icon} />
        </ActionButton>

        <div
          data-testid="multiselect-group-btn"
          className={`${styles.menu_wrapper} ${
            isMenuOpen ? styles.is_open : ""
          }`}
        >
          {search && search.isVisible && (
            <div className={styles.searchFieldWrap}>
              <SearchField
                placeholder={search.placeholder}
                aria-label="searchfield"
                data-testid="searchField"
                onChange={searchHandler}
                UNSAFE_className={styles.searchField}
                {...search.spectrumProps}
              />
            </div>
          )}
          {search && search.isVisible && !list.length && (
            <div className={styles.noResults}>{search.noResults}</div>
          )}
          <CheckboxGroup
            label=""
            aria-label="checkbox-group"
            value={selected}
            onChange={handleChange}
            UNSAFE_className={styles.chkGroup}
            isRequired={required}
            validationState={validationState()}
          >
            {list.map((item: any) => (
              <Checkbox
                UNSAFE_className="groupchkbox"
                value={item.name}
                key={item.id}
                onChange={(val: boolean) => {
                  setCheckedValues({ val, item });
                }}
              >
                {item.name}
              </Checkbox>
            ))}
          </CheckboxGroup>
        </div>
        {hasError && required && (
          <span className={styles.error}>{errors?.required}</span>
        )}
      </div>
    </div>
  );
}

export default withJsonFormsLayoutProps(MultiSelectRenderer);
